<template>
  <div class="main-content">
    <div class="card p-3">
      <div class="page-head">
        <div>
          <div class="page-title-big">Minhas solicitações</div>
        </div>

        <div class="head-actions">
          <button
            class="btn btn-outline-secondary btn-sm top-btn"
            @click="goList"
          >
            Voltar para fila
          </button>
          <button class="btn btn-primary btn-sm top-btn" @click="goNew">
            Nova solicitação
          </button>
        </div>
      </div>

      <div class="summary-grid">
        <div class="summary-card summary-total">
          <div class="summary-number">{{ summary.total }}</div>
          <div class="summary-label">Total</div>
        </div>

        <div class="summary-card summary-progress">
          <div class="summary-number">{{ summary.inProgress }}</div>
          <div class="summary-label">Em andamento</div>
        </div>

        <div class="summary-card summary-approval">
          <div class="summary-number">{{ summary.waitingApproval }}</div>
          <div class="summary-label">Aguardando aprovação</div>
        </div>

        <div class="summary-card summary-approved">
          <div class="summary-number">{{ summary.approved }}</div>
          <div class="summary-label">Aprovados</div>
        </div>

        <div class="summary-card summary-done">
          <div class="summary-number">{{ summary.done }}</div>
          <div class="summary-label">Concluídas</div>
        </div>

        <div class="summary-card summary-rejected">
          <div class="summary-number">{{ summary.rejected }}</div>
          <div class="summary-label">Reprovadas</div>
        </div>
      </div>

      <div class="filters-box">
        <div class="filters-row">
          <input
            v-model="filters.search"
            class="form-control"
            placeholder="Buscar por ID, título ou descrição..."
            @keyup.enter="doSearch"
          />

          <select v-model="filters.status" class="form-control select-filter">
            <option value="">Todos os status</option>
            <option value="in_progress">Em andamento</option>
            <option value="waiting_approval">Aguardando aprovação</option>
            <option value="done">Concluído</option>
            <option value="rejected">Reprovado</option>
            <option value="cancelled">Cancelado</option>
          </select>

          <select v-model="filters.area" class="form-control select-filter">
            <option value="">Todas as áreas</option>
            <option v-for="area in areaOptions" :key="area" :value="area">
              {{ area }}
            </option>
          </select>

          <button class="btn btn-primary" @click="doSearch">Buscar</button>
          <button class="btn btn-light" @click="clearFilters">Limpar</button>
        </div>
      </div>

      <div v-if="error" class="alert alert-danger mt-2">
        {{ error }}
      </div>

      <div class="table-shell mt-3">
        <div v-if="loading" class="empty-row">Carregando...</div>

        <div v-else class="table-responsive">
          <table class="table table-sm table-bordered my-table">
            <thead>
              <tr>
                <th class="col-id">ID</th>
                <th>TÍTULO</th>
                <th class="col-status">STATUS</th>
                <th class="col-step">ETAPA ATUAL</th>
                <th class="col-area">ÁREA ATUAL</th>
                <th class="col-update">ÚLTIMA ATUALIZAÇÃO</th>
                <th class="col-open">ABERTURA</th>
                <th class="actions-th col-actions">AÇÕES</th>
              </tr>
            </thead>

            <tbody>
              <tr v-for="w in filteredRows" :key="w.id">
                <td class="id-cell">{{ w.code }}</td>

                <td class="title-cell">
                  <div class="title-main">{{ w.title }}</div>
                  <div class="title-sub" v-if="w.tag && w.tag !== '—'">
                    Tag: {{ w.tag }}
                  </div>
                </td>

                <td>
                  <span class="badge-status" :class="'st-' + w.status">
                    {{ statusLabel(w.status) }}
                  </span>
                </td>

                <td>{{ w.current_step_name || "—" }}</td>
                <td>{{ w.current_area || "—" }}</td>
                <td>{{ w.updated_at || "—" }}</td>
                <td>{{ w.opened_at || "—" }}</td>

                <td class="actions-td">
                  <div class="actions">
                    <button
                      class="icon-btn btn-view"
                      title="Visualizar"
                      @click="view(w)"
                    >
                      <i class="fa fa-eye"></i>
                    </button>
                    <button
                      v-if="
                        !['done', 'rejected', 'cancelled'].includes(w.status)
                      "
                      class="icon-btn btn-del"
                      title="Cancelar solicitação"
                      @click="cancelFlow(w)"
                    >
                      <i class="fa fa-ban"></i>
                    </button>
                  </div>
                </td>
              </tr>

              <tr v-if="!filteredRows.length">
                <td colspan="8" class="empty-row">
                  Nenhuma solicitação encontrada.
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  data() {
    return {
      loading: false,
      error: "",
      rows: [],
      departments: [],
      filters: {
        search: "",
        status: "",
        area: "",
      },
    };
  },

  async mounted() {
    await this.loadDepartments();
    await this.fetchRows();
  },

  computed: {
    filteredRows() {
      const search = (this.filters.search || "").trim().toLowerCase();
      const status = this.filters.status;
      const area = this.filters.area;

      return this.rows.filter((w) => {
        const matchesSearch =
          !search ||
          (w.code || "").toLowerCase().includes(search) ||
          (w.title || "").toLowerCase().includes(search) ||
          (w.description || "").toLowerCase().includes(search) ||
          (w.tag || "").toLowerCase().includes(search);

        const matchesStatus = !status || w.status === status;
        const matchesArea = !area || w.current_area === area;

        return matchesSearch && matchesStatus && matchesArea;
      });
    },

    areaOptions() {
      return this.departments
        .map((d) => d.name || d.description)
        .filter(Boolean)
        .sort((a, b) => String(a).localeCompare(String(b)));
    },

    summary() {
      return {
        total: this.rows.length,
        inProgress: this.rows.filter((x) => x.status === "in_progress").length,
        waitingApproval: this.rows.filter(
          (x) => x.status === "waiting_approval"
        ).length,
        approved: this.rows.filter((x) => x.last_approval_status === "approved")
          .length,
        done: this.rows.filter((x) => x.status === "done").length,
        rejected: this.rows.filter((x) => x.status === "rejected").length,
      };
    },
  },

  methods: {
    async loadDepartments() {
      try {
        const apiBase = this.$store.state.api;

        const res = await this.$http.get(apiBase + "departments", {
          params: {
            paginated: false,
          },
        });

        this.departments = Array.isArray(res.data?.data)
          ? res.data.data
          : Array.isArray(res.data)
          ? res.data
          : [];
      } catch (e) {
        console.error(e);
        this.departments = [];
      }
    },

    async fetchRows() {
      try {
        this.loading = true;
        this.error = "";

        const apiBase = this.$store.state.api;

        const res = await this.$http.get(apiBase + "flows", {
          params: {
            mine: 1,
            department_id: "all",
          },
        });

        const data = Array.isArray(res.data?.data)
          ? res.data.data
          : res.data || [];

        this.rows = data.map((f) => ({
          id: f.id,
          code: `#${f.id}`,
          title: f.title || "—",
          description: f.description || "",
          tag: f.tag || "—",
          status: f.status || "new",
          last_approval_status: f.last_approval_status || "",
          current_step_name: f.current_step_name || "—",
          current_area: f.current_department_name || "—",
          opened_at: this.formatDate(f.created_at),
          updated_at: this.formatDate(f.updated_at),
          opened_by_user_id: f.opened_by_user_id,
        }));
      } catch (e) {
        console.error(e);
        this.error = "Não consegui carregar suas solicitações.";
        this.rows = [];
      } finally {
        this.loading = false;
      }
    },

    doSearch() {},

    clearFilters() {
      this.filters.search = "";
      this.filters.status = "";
      this.filters.area = "";
    },

    formatDate(value) {
      if (!value) return "—";

      const d = new Date(value);
      if (isNaN(d.getTime())) return value;

      return d.toLocaleString("pt-BR");
    },

    statusLabel(status) {
      const map = {
        new: "Novo",
        in_progress: "Em andamento",
        waiting_approval: "Aguardando aprovação",
        rejected: "Reprovado",
        done: "Concluído",
        cancelled: "Cancelado",
      };
      return map[status] || status || "—";
    },

    goList() {
      this.$router.push({ name: "list-flow" });
    },

    goNew() {
      this.$router.push("/Flow/new");
    },

    view(w) {
      this.$router.push(`/Flow/view/${w.id}`);
    },

    async cancelFlow(w) {
      const confirm = await swal({
        title: "Cancelar solicitação?",
        text: `Tem certeza que deseja cancelar "${
          w.title || "#" + w.id
        }"? Esta ação não pode ser desfeita.`,
        icon: "warning",
        buttons: ["Não", "Sim, cancelar"],
        dangerMode: true,
      });

      if (!confirm) return;

      try {
        const apiBase = this.$store.state.api;
        await this.$http.post(apiBase + "flows/" + w.id + "/cancel");
        swal(
          "Cancelado",
          "A solicitação foi cancelada com sucesso.",
          "success"
        );
        await this.fetchRows();
      } catch (e) {
        swal(
          "Erro",
          e?.response?.data?.message || "Erro ao cancelar.",
          "error"
        );
      }
    },
  },
};
</script>

<style scoped>
.page-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 18px;
}

.page-title-big {
  font-size: 32px;
  font-weight: 800;
  color: #374151;
  line-height: 1.1;
}

.page-subtitle {
  font-size: 15px;
  color: #6b7280;
  margin-top: 6px;
}

.head-actions {
  display: flex;
  gap: 12px;
  align-items: center;
}

.top-btn {
  width: 165px;
  height: 32px;
  padding: 0 12px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  white-space: nowrap;
}

.summary-grid {
  display: grid;
  grid-template-columns: repeat(6, minmax(150px, 1fr));
  gap: 14px;
  margin-bottom: 18px;
}

.summary-card {
  border-radius: 12px;
  border: 1px solid #e5e7eb;
  padding: 18px;
  background: #fff;
}

.summary-number {
  font-size: 38px;
  font-weight: 800;
  line-height: 1;
  margin-bottom: 8px;
}

.summary-label {
  font-size: 15px;
  font-weight: 700;
  color: #374151;
}

.summary-total {
  background: #f7f8fc;
}

.summary-progress {
  background: #fff8eb;
}

.summary-approval {
  background: #f3e8ff;
}

.summary-approved {
  background: #ecfdf3;
}

.summary-done {
  background: #dcfce7;
}

.summary-rejected {
  background: #fff1f1;
}

.summary-total .summary-number {
  color: #374151;
}

.summary-progress .summary-number {
  color: #c78b11;
}

.summary-approval .summary-number {
  color: #7c3aed;
}

.summary-approved .summary-number,
.summary-done .summary-number {
  color: #15803d;
}

.summary-rejected .summary-number {
  color: #b91c1c;
}

.filters-box {
  background: #fff;
  border: 1px solid #eef2f7;
  border-radius: 12px;
  padding: 14px;
}

.filters-row {
  display: flex;
  gap: 12px;
  align-items: center;
}

.filters-row .form-control {
  height: 42px;
  border-radius: 8px;
}

.select-filter {
  max-width: 220px;
}

.filters-row .btn {
  height: 42px;
  border-radius: 8px;
  padding: 0 18px;
  font-weight: 700;
}

.table-shell {
  background: #fff;
  border: 1px solid #eef2f7;
  border-radius: 12px;
  padding: 10px;
}

.my-table thead th {
  background: #f6f7fb;
  color: #6b7280;
  font-weight: 800;
  font-size: 12px;
  letter-spacing: 0.03em;
  border-color: #e5e7eb;
}

.my-table td {
  vertical-align: middle;
  border-color: #e5e7eb;
  font-weight: 600;
  color: #4b5563;
}

.col-id {
  width: 110px;
}
.col-status {
  width: 200px;
}
.col-step {
  width: 220px;
}
.col-area {
  width: 170px;
}
.col-update {
  width: 170px;
}
.col-open {
  width: 170px;
}
.col-actions {
  width: 100px;
  text-align: center;
}

.id-cell {
  font-weight: 900;
  color: #6b7280;
}

.title-main {
  font-weight: 700;
  color: #374151;
}

.title-sub {
  font-size: 12px;
  color: #9ca3af;
  margin-top: 3px;
}

.empty-row {
  text-align: center;
  padding: 18px !important;
  color: #9ca3af;
  font-weight: 700;
}

.actions-th,
.actions-td {
  text-align: center;
  white-space: nowrap;
}

.actions {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.icon-btn {
  width: 28px;
  height: 28px;
  padding: 0;
  border-radius: 6px;
  background: #fff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  border: 2px solid #ccc;
  margin: 0;
  line-height: 1;
}

.icon-btn i {
  font-size: 12px;
  color: #111;
}

.btn-view {
  border-color: #f1c21b;
}

.icon-btn:hover {
  filter: brightness(0.95);
  transform: translateY(-1px);
  transition: 0.12s;
}

.badge-status {
  padding: 4px 10px;
  border-radius: 999px;
  font-weight: 600;
  font-size: 12px;
  border: 1px solid #d1d5db;
}

.st-new {
  background: #e8f0ff;
  color: #1d4ed8;
}

.st-in_progress {
  background: #fff7e6;
  color: #a16207;
}

.st-waiting_approval {
  background: #f3e8ff;
  color: #7c3aed;
}

.st-rejected {
  background: #ffe3e3;
  color: #b91c1c;
}

.st-done {
  background: #dcfce7;
  color: #15803d;
}
</style>
